import {Component, EventEmitter, Input, Output} from '@angular/core';

import {VerificationCardState} from './verification-card-state';

/**
 * Presentational card for a single verification (KYC or AVS) on the ecommerce landing page.
 * Renders the icon, title, status chip, description and footer note, plus an optional call to
 * action button when a label is supplied.
 */
@Component({
    selector: 'app-verification-card',
    templateUrl: './verification-card.component.html',
    styleUrls: ['./verification-card.component.scss'],
    standalone: false
})
export class VerificationCardComponent
{
    /** Font Awesome icon class shown beside the title. */
    @Input() iconClass: string;
    /** Card title (e.g. 'KYC verification'). */
    @Input() title: string;
    /** Current verification state, driving the status chip. */
    @Input() cardState: VerificationCardState;
    /** Body copy describing the verification. */
    @Input() description: string;
    /** Footer note (e.g. 'Takes ~5 minutes'). */
    @Input() footerNote: string;
    /** Action button label; the button is hidden when not supplied. */
    @Input() actionLabel?: string;
    /** Whether the action button is disabled. */
    @Input() actionDisabled = false;
    /** Secondary action label (e.g. 'Get a new verification link'); the secondary button is hidden when not supplied. */
    @Input() secondaryActionLabel?: string;
    /** Whether the secondary action button is disabled. */
    @Input() secondaryActionDisabled = false;
    /** Visual accent for the icon badge: 'primary' (navy) for KYC, 'accent' (orange) for AVS. */
    @Input() iconAccent: 'primary' | 'accent' = 'primary';
    /** Emits when the action button is clicked. */
    @Output() actionClick = new EventEmitter<void>();
    /** Emits when the secondary action button is clicked. */
    @Output() secondaryActionClick = new EventEmitter<void>();

    get stateChipLabel(): string
    {
        switch (this.cardState)
        {
            case 'UNDER_REVIEW':
                return 'Under Review';
            case 'FAILED':
                return 'Failed';
            case 'VERIFIED':
                return 'Verified';
            case 'ERROR':
                return 'Unavailable';
            default:
                return 'Not Started';
        }
    }

    get stateChipClass(): string
    {
        switch (this.cardState)
        {
            case 'UNDER_REVIEW':
                return 'status-chip under-review';
            case 'FAILED':
                return 'status-chip failed';
            case 'VERIFIED':
                return 'status-chip verified';
            case 'ERROR':
                return 'status-chip error';
            default:
                return 'status-chip not-started';
        }
    }
}
